import { useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Bell, LogOut, Menu, ShoppingCart, User, X } from 'lucide-react';
import { useTranslation } from '@most/i18n';
import { useLogout, useMe, LOGIN_PATH } from '@most/auth';
import { Button, cn } from '@most/ui';
import { useCart } from '../../api/cart';
import { LocaleSwitcher } from './LocaleSwitcher';

/** docs/06 §4: the unread count in the bell badge caps at "9+". */
function badgeText(count: number): string | null {
  if (count <= 0) return null;
  return count > 9 ? '9+' : String(count);
}

export function Shell({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common');
  const { data: me } = useMe();
  const { data: cart } = useCart();
  const logout = useLogout();
  const [menuOpen, setMenuOpen] = useState(false);

  const cartCount = cart?.lines.length ?? 0;
  const unread = badgeText(me?.unread_notifications ?? 0);

  const handleLogout = () => {
    logout.mutate(undefined, {
      // Login lives in the landing app on another origin — a full-page navigation.
      onSettled: () => window.location.assign(LOGIN_PATH),
    });
  };

  const navItems = [
    { to: '/catalog', label: t('nav.catalog') },
    { to: '/orders', label: t('nav.orders') },
    { to: '/notifications', label: t('nav.notifications') },
    { to: '/profile', label: t('nav.profile') },
  ];

  return (
    <div className="flex min-h-dvh flex-col bg-app">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-card focus:px-4 focus:py-2 focus:shadow"
      >
        {t('a11y.skipToContent')}
      </a>

      {/*
       * The header is the admin's navigation RAIL, laid on its side.
       *
       * Merdan's operators and his buyers are looking at one business, and the near-black bar is
       * what makes the two apps read as one brand — the eye lands on "navigation ‖ goods" before it
       * reads a word. It is also why the rail's whole token scale (`rail`, `rail-foreground`,
       * `rail-muted`, …) lives in this app: on this one surface the foreground is light-on-dark and
       * every other token's contrast assumption inverts, focus ring included (see styles.css).
       *
       * It stays a top bar rather than becoming an actual left rail because this is a catalog read
       * on a phone. A sidebar would cost the product grid its horizontal room and collapse to a
       * drawer on exactly the device most buyers use.
       */}
      <header className="sticky top-0 z-40 border-b border-rail-border bg-rail text-rail-foreground">
        <div className="mx-auto flex h-16 w-full max-w-7xl items-center gap-3 px-4">
          {/* The wordmark is the one place the brand yellow gets to be TEXT — it is a display
              weight at 18 px on near-black, which clears AA comfortably. Everywhere else on a
              light surface it would not, which is what `--link` exists for. */}
          <Link
            to="/catalog"
            className="rounded text-lg font-extrabold tracking-tight text-primary"
          >
            {t('brand')}
          </Link>

          <nav aria-label={t('nav.menu')} className="ml-6 hidden items-center gap-1 md:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    // The active item is a lit chip, not merely brighter text: on a dark bar a
                    // weight change alone is too quiet to answer "where am I".
                    isActive
                      ? 'bg-rail-hover text-rail-active'
                      : 'text-rail-muted hover:bg-rail-hover hover:text-rail-foreground',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-1">
            <LocaleSwitcher />

            <IconLink
              to="/notifications"
              label={
                unread
                  ? t('a11y.unreadCount', { count: me?.unread_notifications ?? 0 })
                  : t('nav.notifications')
              }
              badge={unread}
              badgeTone="danger"
            >
              <Bell className="h-5 w-5" aria-hidden="true" />
            </IconLink>

            <IconLink
              to="/cart"
              label={t('nav.cart')}
              badge={cartCount > 0 ? String(cartCount) : null}
              badgeTone="primary"
            >
              <ShoppingCart className="h-5 w-5" aria-hidden="true" />
            </IconLink>

            {/* `ghost` on the dark bar would hover to a light `muted` — the shared kit's variants
                are tuned for the light canvas. These two buttons opt out and take the rail's own
                hover instead, which is the same trade the admin's rail makes. */}
            <Button
              variant="ghost"
              size="icon"
              className="hidden text-rail-muted hover:bg-rail-hover hover:text-rail-foreground md:inline-flex"
              onClick={handleLogout}
              aria-label={t('nav.logout')}
              title={t('nav.logout')}
            >
              <LogOut className="h-5 w-5" aria-hidden="true" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="text-rail-muted hover:bg-rail-hover hover:text-rail-foreground md:hidden"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              aria-label={menuOpen ? t('a11y.closeMenu') : t('a11y.openMenu')}
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {menuOpen && (
          <nav
            id="mobile-nav"
            aria-label={t('nav.menu')}
            className="border-t border-rail-border bg-rail md:hidden"
          >
            <div className="mx-auto flex max-w-7xl flex-col p-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      // ≥ 44 px touch targets in the buyer portal (docs/06 §9).
                      'flex min-h-11 items-center rounded-md px-3 text-sm font-medium',
                      isActive
                        ? 'bg-rail-hover text-rail-active'
                        : 'text-rail-muted hover:bg-rail-hover hover:text-rail-foreground',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <button
                type="button"
                onClick={handleLogout}
                className="flex min-h-11 items-center gap-2 rounded-md px-3 text-left text-sm font-medium text-rail-muted hover:bg-rail-hover hover:text-rail-foreground"
              >
                <User className="h-4 w-4" aria-hidden="true" />
                {t('nav.logout')}
              </button>
            </div>
          </nav>
        )}
      </header>

      <main id="main" className="flex-1">
        {children}
      </main>

      <footer className="border-t border-border py-6">
        <div className="mx-auto max-w-7xl px-4 text-xs text-muted-foreground">
          {t('brand')} · USD
        </div>
      </footer>
    </div>
  );
}

function IconLink({
  to,
  label,
  badge,
  badgeTone,
  children,
}: {
  to: string;
  label: string;
  badge: string | null;
  badgeTone: 'primary' | 'danger';
  children: ReactNode;
}) {
  return (
    <Button
      asChild
      variant="ghost"
      size="icon"
      className="relative text-rail-muted hover:bg-rail-hover hover:text-rail-foreground"
    >
      <Link to={to} aria-label={label} title={label}>
        {children}
        {badge && (
          <span
            className={cn(
              'absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none',
              // The ink is per-tone, not a blanket `text-white`. Red carries white; the brand's
              // butter yellow CANNOT — white on it fails AA badly, so the cart count is dark ink on
              // yellow. That is the whole reason `--primary-foreground` exists (see styles.css).
              badgeTone === 'danger'
                ? 'bg-destructive text-destructive-foreground'
                : 'bg-primary text-primary-foreground',
            )}
          >
            {badge}
          </span>
        )}
      </Link>
    </Button>
  );
}
